import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance } from "../../../../utils/constants/accountType";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import FormTextarea from "../../../../components/Form/TextArea";
import CheckboxField from "../../../../components/Form/CheckboxField";
import { getTimezonesWithOffset } from "../../../../utils/constants/timezones";
import { getCountries } from "../../../../utils/constants/countries";
import { btn } from "../../../../components/Button/buttonStyles";

const staticTimezones = getTimezonesWithOffset();
const clientTypes = ["Company", "Individual"];
const statuses = ["Active", "Inactive", "Pending"];
const genders = ["Male", "Female", "Other"];
const legalEntities = [
  "Private Limited",
  "LLP",
  "Sole Proprietorship",
  "Partnership",
];
const countries = getCountries();

const CreateClientForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Freelance accounts cannot grant clients access to the client portal.
  const freelance = isFreelance(user);

  const [form, setForm] = useState({
    type: "",
    company_name: "",
    legal_entity: "",
    status: "Active",
    country: "",
    state_region: "",
    city: "",
    postal_code: "",
    address: "",
    pan_tax_number: "",
    gstin_vat_number: "",
    website: "",
    note: "",
    can_login: !isFreelance(user),
    email: "",

    first_name: "",
    last_name: "",
    timezone: "",
    phone: "",
    zoom_id: "",
    teams_id: "",
    gender: "",
    nationality: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");

  const validate = () => {
    const currentErrors = {};
    if (form.type === "Company" && !form.company_name)
      currentErrors.company_name = "Company name is required.";
    if (!freelance && !form.legal_entity)
      currentErrors.legal_entity = "Legal Entity is required.";
    if (!form.status) currentErrors.status = "Status is required.";
    if (!form.country) currentErrors.country = "Country is required.";
    if (!form.email) currentErrors.email = "Email is required.";
    if (!form.first_name) currentErrors.first_name = "First name is required.";
    if (!form.last_name) currentErrors.last_name = "Last name is required.";
    if (!form.timezone) currentErrors.timezone = "Timezone is required.";
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const requiredFields = [
    "type",
    ...(freelance ? [] : ["legal_entity"]),
    "status",
    "country",
    "first_name",
    "last_name",
    "email",
    "timezone",
  ];

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";

    if (
      (name === "company_name" && form.type === "Company" && !value) ||
      (requiredFields.includes(name) && !value)
    ) {
      errorMsg =
        name === "email"
          ? "Email is required."
          : `${name
              .replace("_", " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())} is required.`;
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const payload = freelance
        ? { ...form, can_login: false, legal_entity: "" }
        : form;
      const response = await api.post("/clients", payload, {
        withCredentials: true,
      });
      setSuccess(response.data.message);
      setServerError("");
      navigate("/clients");
    } catch (err) {
      console.error(err.response?.data || err);
      setServerError(
        err.response?.data?.message ||
          "Something went wrong while creating client."
      );
      setSuccess("");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-6"
    >
      <section className="space-y-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Client Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
        <FormSelect
          label="Client Type"
          name="type"
          value={form.type}
          onChange={handleChange}
          onBlur={handleBlur}
          options={clientTypes}
          error={errors.type}
          required
        />

        {form.type === "Company" ? (
          <FormInput
            label="Company Name"
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.company_name}
            required
          />
        ) : (
          <div />
        )}

        {/* Freelance accounts do not record a legal entity for their clients. */}
        {!freelance && (
          <FormSelect
            label="Legal Entity"
            name="legal_entity"
            value={form.legal_entity}
            onChange={handleChange}
            onBlur={handleBlur}
            options={legalEntities}
            error={errors.legal_entity}
            required
          />
        )}

        <FormSelect
          label="Status"
          name="status"
          value={form.status}
          onChange={handleChange}
          onBlur={handleBlur}
          options={statuses}
          error={errors.status}
          required
        />

        <FormSelect
          label="Country"
          name="country"
          value={form.country}
          onChange={handleChange}
          onBlur={handleBlur}
          options={countries}
          error={errors.country}
          required
        />

        <FormInput
          label="State/Province/Region"
          name="state_region"
          value={form.state_region}
          onChange={handleChange}
        />

        <FormInput
          label="City"
          name="city"
          value={form.city}
          onChange={handleChange}
        />

        <FormInput
          label="Address"
          name="address"
          value={form.address}
          onChange={handleChange}
        />

        <FormInput
          label="Postal/ZIP Code"
          name="postal_code"
          value={form.postal_code}
          onChange={handleChange}
        />

        <FormInput
          label="PAN/Tax Number"
          name="pan_tax_number"
          value={form.pan_tax_number}
          onChange={handleChange}
        />
        <FormInput
          label="GSTIN/VAT Number"
          name="gstin_vat_number"
          value={form.gstin_vat_number}
          onChange={handleChange}
        />
        <FormInput
          label="Website"
          name="website"
          value={form.website}
          onChange={handleChange}
        />

        <FormTextarea
          label="Note"
          name="note"
          value={form.note}
          onChange={handleChange}
          rows={1}
          error={errors.note}
          required={false}
        />

        </div>
      </section>

      <section className="space-y-4 pt-2 border-t border-gray-100">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Primary User Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
        <FormInput
          label="First Name"
          name="first_name"
          value={form.first_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.first_name}
          required
        />

        <FormInput
          label="Last Name"
          name="last_name"
          value={form.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.last_name}
          required
        />

        <FormInput
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email}
          required
        />

        <FormSelect
          label="Timezone"
          name="timezone"
          value={form.timezone}
          onChange={handleChange}
          onBlur={handleBlur}
          options={staticTimezones}
          error={errors.timezone}
          required
        />

        <FormSelect
          label="Gender"
          name="gender"
          value={form.gender}
          onChange={handleChange}
          options={genders}
          error={errors.gender}
        />

        <FormInput
          label="Phone"
          name="phone"
          value={form.phone}
          onChange={handleChange}
        />
        <FormInput
          label="Zoom ID"
          name="zoom_id"
          value={form.zoom_id}
          onChange={handleChange}
        />
        <FormInput
          label="Teams ID"
          name="teams_id"
          value={form.teams_id}
          onChange={handleChange}
        />

          <FormInput
            label="Nationality"
            name="nationality"
            value={form.nationality}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.nationality}
          />
        </div>

        {!freelance && (
          <CheckboxField
            label="Can Login"
            name="can_login"
            checked={form.can_login}
            onChange={handleChange}
          />
        )}
      </section>

      {serverError && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded">
          {serverError}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 rounded">
          {success}
        </div>
      )}

      <div className="pt-3 border-t border-gray-100 space-y-3">
        <p className="text-xs text-gray-500">
          Fields marked with <span className="text-danger-500">*</span> are mandatory.
        </p>
        <div className="flex gap-2 text-sm">
          <button
            type="submit"
            className={btn("primary", "sm")}
          >
            Submit
          </button>
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className={btn("secondary", "sm")}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateClientForm;
